/**
 * The service descriptions the firm actually writes, ready to pick and alter.
 *
 * Taken from the 26 invoices Lee Humble issued on paper between July and
 * September 2026 -- the wording is the firm's own, not a generic
 * accounting-practice template. The counts behind the ordering: the T2 return
 * line appeared on 15 of them, "Discussion and advising as to income tax and
 * related financial matters" on 7, the compilation report and the accounting
 * assistance lines on 5 and 4.
 *
 * A preset is a starting point. The firm's invoices name the specific work
 * done -- "including analysis of leases and prepaid expenses", "for Raider
 * Transport and RTI Excavating" -- so these are deliberately the stems people
 * extend, not finished sentences nobody edits.
 *
 * The live lists are now kept in the database and edited in Settings
 * (/api/invoice-description-presets/). The arrays below are the FALLBACK: what
 * the picker offers while that list loads or if it cannot be fetched, so
 * writing an invoice never depends on it. Migration invoicing 0024 seeded the
 * database from exactly these arrays.
 */

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Wide enough to look like something to fill in rather than a typo.
const BLANK = "__________";

export const CORPORATE_PRESETS = [
  {
    label: "T2 corporate return",
    text:
      "Preparation of {YEAR} T2 corporate income tax return, related schedules " +
      "and GIFI report as required by CRA.",
  },
  {
    label: "Financial statements",
    text:
      "Preparation of unaudited financial statements for the year ended " +
      "{YEAR_END} including review and processing year end adjustments.",
  },
  {
    label: "Accounting assistance",
    text:
      "Accounting assistance preliminary to preparation of unaudited financial " +
      "statements.",
  },
  {
    label: "Compilation report",
    text:
      "Professional services related to the issuance of the Compilation " +
      "Engagement Report.",
  },
  {
    label: "Income tax advice",
    text: "Discussion and advising as to income tax and related financial matters.",
  },
  {
    label: "Review and discussions",
    text:
      "Review, analysis and discussions concerning income tax and financial " +
      "related matters.",
  },
  {
    label: "Year end adjustments",
    text: "Analysis and processing year end adjustments.",
  },
  {
    label: "T5 dividend summary",
    text: "Preparation of T5 summary for dividends paid.",
  },
  {
    label: "GST return",
    text: "Analysis and preparation of GST return.",
  },
  {
    label: "Shareholder T-1 returns",
    text: "Preparation of personal T-1 income tax returns.",
  },
];

export const PERSONAL_PRESETS = [
  {
    label: "T-1 personal returns",
    text: "Preparation of {YEAR} T-1 income tax returns and related schedules.",
  },
  {
    label: "Income tax advice",
    text: "Discussions and advising as to income tax and financial related matters.",
  },
  {
    label: "Business statements",
    text:
      "Review of business information and analysis of deductions available and " +
      "preparation of business statements.",
  },
  {
    label: "T1163 farming",
    text: "Preparation of schedule T1163 for farming activities.",
  },
  {
    label: "Credits and deductions",
    text:
      "Optimization of available tax credits and deductions for carry forward.",
  },
  {
    label: "Financial statements",
    text:
      "Analysis and preparation of unaudited financial statements for the year " +
      "ended {YEAR_END}.",
  },
  {
    label: "Compilation report",
    text:
      "Procedures relating to the issuance of the Compilation Engagement Report.",
  },
  {
    label: "GST returns",
    text: "Preparation and filing of GST returns to date.",
  },
];

/**
 * The firm's own year end wording for a client, or "" if nobody knows it.
 *
 * 81 corporate clients in production carry a NULL fiscal_month_end. Defaulting
 * those to December would print a year end the client knows is wrong, which is
 * worse than a blank somebody fills in.
 */
export function yearEndWording(client, year) {
  if (!year) return "";
  if (!client) return "";
  if (!client.is_corporate) return `December 31, ${year}`;
  const month = client.fiscal_month_end;
  if (!month || month < 1 || month > 12) return "";
  // Day 0 of the next month is the last day of this one, leap years included.
  const lastDay = new Date(year, month, 0).getDate();
  return `${MONTHS[month - 1]} ${lastDay}, ${year}`;
}

/** A preset with its placeholders filled in, or left as a blank to complete. */
export function fillPreset(text, { year, yearEndDate, yearEnd: wording } = {}) {
  let yearEnd = wording || "";
  if (!yearEnd && yearEndDate) {
    // "2026-05-31" -- parsed by hand, because new Date() on a bare date string
    // reads it as UTC and can report the day before in a western timezone.
    const [y, m, d] = String(yearEndDate).split("-").map(Number);
    if (y && m >= 1 && m <= 12 && d) yearEnd = `${MONTHS[m - 1]} ${d}, ${y}`;
  }
  return String(text || "")
    .replace(/\{YEAR\}/g, year ? String(year) : BLANK)
    .replace(/\{YEAR_END\}/g, yearEnd || BLANK);
}

const FALLBACK_LISTS = { corporate: CORPORATE_PRESETS, personal: PERSONAL_PRESETS };

/**
 * Both lists, the client's own kind first.
 *
 * `lists` is {corporate, personal} as made by presetListsFrom(); omitted or
 * null, the built-in arrays above are used.
 *
 * Never one list only: the firm's corporate invoices routinely carry the
 * shareholder's personal return, and its personal invoices carry financial
 * statements for a proprietorship.
 */
export function presetsFor(client, lists) {
  const source = lists || FALLBACK_LISTS;
  const corporate = { name: "Corporate", presets: source.corporate || [] };
  const personal = { name: "Personal", presets: source.personal || [] };
  const groups = client && client.is_corporate === false
    ? [personal, corporate]
    : [corporate, personal];
  // A list a manager has emptied out is left off rather than shown as an
  // empty heading.
  return groups.filter((group) => group.presets.length > 0);
}

/**
 * The API's rows as the two lists presetsFor takes, or null if `rows` is not
 * a usable answer -- null meaning "use the built-in lists".
 *
 * Only active rows, in sort order. Anything that is not an array (a failed
 * request, an error body, a paginated envelope) is treated as no answer, so a
 * hiccup on this endpoint can never leave the picker empty.
 */
export function presetListsFrom(rows) {
  if (!Array.isArray(rows)) return null;
  const byOrder = (a, b) =>
    (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id ?? 0) - (b.id ?? 0);
  const pick = (category) =>
    rows
      .filter((r) => r && r.category === category && r.is_active !== false
        && typeof r.text === "string" && r.text)
      .sort(byOrder)
      .map((r) => ({ id: r.id, label: r.label, text: r.text }));
  return { corporate: pick("corporate"), personal: pick("personal") };
}

/**
 * Which preset a line is currently using, as the raw template text, or "".
 *
 * Derived rather than remembered: the picker shows the preset whose FILLED
 * wording is exactly what the line says. Two things fall out of that, both
 * wanted. A line the user has altered stops being claimed by any preset --
 * altering it is the point of a preset, and the picker should not go on
 * asserting the firm's standard wording is still there. And because the
 * select's value then changes, re-picking the same preset fires again, which
 * is how an edited line gets reset.
 */
export function presetValueFor(description, context, lists) {
  const text = String(description || "");
  if (!text) return "";
  for (const group of presetsFor(null, lists)) {
    for (const preset of group.presets) {
      if (fillPreset(preset.text, context) === text) return preset.text;
    }
  }
  return "";
}
